import React from 'react';
import PriceCalculator from './PriceCalculator';
import NumberInput from './NumberInput';
import { formatMoney, formatNumber } from './numberFormat';

// Manual Shipping/Insurance Cost entry on a Quote/Sales Order — a plain
// typed field paired with the shared PriceCalculator. Typed/displayed as
// Inc-GST whenever GST applies (these dollar figures are usually known
// GST-inclusive — a carrier/insurer's quoted price) and as a plain amount
// when it doesn't (e.g. an export Customer). No separate Ex-GST/Inc-GST
// toggle — PriceCalculator's own Tax+/Tax− keys already cover converting
// a known ex-GST figure into inc-GST before committing it. The value/
// onCommit pair the caller holds is always ex-GST, matching quote_lines/
// sales_order_lines' own storage convention. Locked (Sales Order converted
// from an Accepted Quote) renders a plain read-only figure instead — the
// customer already approved this amount on the Quote.
function GstAwareCostField({ value, onCommit, gstApplicable, gstRate, locked }) {
  const rate = Number(gstRate) || 0;

  // The figure as typed/shown — Inc-GST when GST applies — rounded to
  // cents so focusing the field doesn't reveal a long ex-GST division.
  const shownValue = (exValue) => {
    if (exValue === null || exValue === undefined || exValue === '') return '';
    const shown = gstApplicable ? Number(exValue) * (1 + rate / 100) : Number(exValue);
    return Math.round(shown * 100) / 100;
  };

  const commitDraft = (raw) => {
    if (raw === '' || raw === undefined || raw === null) { onCommit(null); return; }
    const typed = Number(raw);
    if (!Number.isFinite(typed)) return;
    const exValue = gstApplicable && rate ? typed / (1 + rate / 100) : typed;
    onCommit(Math.round(exValue * 1000) / 1000);
  };

  if (locked) {
    return (
      <span style={{ fontSize: 14 }}>
        {value === null || value === undefined ? '—' : formatMoney(shownValue(value))}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <NumberInput
        value={shownValue(value)}
        decimals={2}
        min="0"
        placeholder={formatNumber(0, { decimals: 2 })}
        onBlur={(e) => {
          // Blurring an untouched field doesn't re-save a rounded figure.
          if (e.target.value === String(shownValue(value))) return;
          commitDraft(e.target.value);
        }}
        style={{ width: 80, fontSize: 13, textAlign: 'right' }}
      />
      <PriceCalculator
        initialValue={gstApplicable && value !== null && value !== undefined ? Number(value) * (1 + rate / 100) : value}
        onResult={(val) => commitDraft(val)}
      />
    </div>
  );
}

export default GstAwareCostField;
